import { create } from 'zustand';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useBatteryStore = create((set, get) => ({
  // Cell catalog
  cells: [],
  selectedCellId: '18650',
  
  // BMS catalog
  bmsModels: [],
  selectedBmsId: 'BMS-10S-30A',
  includeBms: false,
  
  // Configuration
  config: {
    series: 10,
    parallel: 4,
    layers: 1,
    parablock_rows: 2,
    parablock_cols: 2,
    spacing_mm: 1.0,
    max_length_mm: null,
    max_width_mm: null,
    max_height_mm: null,
  },
  
  // Generation constraints
  constraints: {
    min_series: 1,
    max_series: 20,
    min_parallel: 1,
    max_parallel: 20,
    target_voltage: null,
    target_capacity_wh: null,
  },
  
  // Results
  results: [],
  selectedResult: null,
  isGenerating: false,
  generationTime: 0,
  
  // UI State
  activeTab: 'specs',
  viewMode: '3d', // '3d' | 'routing'
  showGrid: false,
  showAxes: false,
  showLabels: false,
  
  // Drafts
  drafts: [],
  currentDraftId: null,
  
  // Actions
  setSelectedCell: (cellId) => set({ selectedCellId: cellId }),
  setSelectedBms: (bmsId) => set({ 
    selectedBmsId: bmsId,
    // Clear results when BMS changes to prompt regeneration
    results: [],
    selectedResult: null
  }),
  setIncludeBms: (include) => set({ 
    includeBms: include,
    // Clear results when BMS toggle changes to prompt regeneration
    results: [],
    selectedResult: null
  }),
  
  updateConfig: (updates) => set((state) => ({
    config: { ...state.config, ...updates }
  })),
  
  updateConstraints: (updates) => set((state) => ({
    constraints: { ...state.constraints, ...updates }
  })),
  
  setActiveTab: (tab) => set({ activeTab: tab }),
  setViewMode: (mode) => set({ viewMode: mode }),
  toggleGrid: () => set((state) => ({ showGrid: !state.showGrid })),
  toggleAxes: () => set((state) => ({ showAxes: !state.showAxes })),
  toggleLabels: () => set((state) => ({ showLabels: !state.showLabels })),
  
  // Fetch cells from API
  fetchCells: async () => {
    try {
      const response = await fetch(`${API_URL}/api/cells`);
      const data = await response.json();
      set({ cells: data });
    } catch (error) {
      console.error('Failed to fetch cells:', error);
    }
  },
  
  // Fetch BMS models from API
  fetchBmsModels: async () => {
    try {
      const response = await fetch(`${API_URL}/api/bms`);
      const data = await response.json();
      set({ bmsModels: data });
    } catch (error) {
      console.error('Failed to fetch BMS models:', error);
    }
  },
  
  // Generate pack configurations
  generatePacks: async () => {
    const state = get();
    set({ isGenerating: true, results: [], selectedResult: null });
    
    try {
      const response = await fetch(`${API_URL}/api/packs/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cell_id: state.selectedCellId,
          target_series: state.config.series,
          target_parallel: state.config.parallel,
          min_series: state.constraints.min_series,
          max_series: state.constraints.max_series,
          min_parallel: state.constraints.min_parallel,
          max_parallel: state.constraints.max_parallel,
          max_length_mm: state.config.max_length_mm,
          max_width_mm: state.config.max_width_mm,
          max_height_mm: state.config.max_height_mm,
          target_voltage: state.constraints.target_voltage,
          target_capacity_wh: state.constraints.target_capacity_wh,
          max_results: 100,
          include_bms: state.includeBms,
          bms_id: state.includeBms ? state.selectedBmsId : null,
        }),
      });
      
      const data = await response.json();
      set({
        results: data.results,
        generationTime: data.generation_time_ms,
        selectedResult: data.results[0] || null,
        isGenerating: false,
      });
    } catch (error) {
      console.error('Failed to generate packs:', error);
      set({ isGenerating: false });
    }
  },
  
  selectResult: (result) => set({ selectedResult: result }),
  
  // Draft management (localStorage)
  loadDrafts: () => {
    try {
      const drafts = JSON.parse(localStorage.getItem('battery_drafts') || '[]');
      set({ drafts });
    } catch (error) {
      console.error('Failed to load drafts:', error);
      set({ drafts: [] });
    }
  },
  
  saveDraft: (name) => {
    const state = get();
    const draft = {
      id: Date.now().toString(),
      name: name || `Draft ${new Date().toLocaleDateString()}`,
      createdAt: new Date().toISOString(),
      selectedCellId: state.selectedCellId,
      config: state.config,
      constraints: state.constraints,
      selectedResult: state.selectedResult,
      includeBms: state.includeBms,
      selectedBmsId: state.selectedBmsId,
    };
    
    const drafts = [...state.drafts, draft];
    localStorage.setItem('battery_drafts', JSON.stringify(drafts));
    set({ drafts, currentDraftId: draft.id });
    return draft;
  },
  
  loadDraft: (draftId) => {
    const state = get();
    const draft = state.drafts.find(d => d.id === draftId);
    if (draft) {
      set({
        selectedCellId: draft.selectedCellId,
        config: draft.config,
        constraints: draft.constraints,
        selectedResult: draft.selectedResult,
        currentDraftId: draft.id,
        includeBms: draft.includeBms || false,
        selectedBmsId: draft.selectedBmsId || 'BMS-10S-30A',
      });
    }
  },
  
  deleteDraft: (draftId) => {
    const state = get();
    const drafts = state.drafts.filter(d => d.id !== draftId);
    localStorage.setItem('battery_drafts', JSON.stringify(drafts));
    set({ drafts });
  },
  
  // Export functions
  exportJSON: () => {
    const state = get();
    if (!state.selectedResult) return null;
    
    const exportData = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      cell: state.cells.find(c => c.id === state.selectedCellId),
      config: state.selectedResult.config,
      specs: state.selectedResult.specs,
      layout: state.selectedResult.layout,
      validation: state.selectedResult.validation,
      bom: state.selectedResult.bom,
    };
    
    return exportData;
  },
  
  exportCSV: () => {
    const state = get();
    if (!state.selectedResult) return null;
    
    const bom = state.selectedResult.bom;
    const headers = ['Component', 'Quantity', 'Unit', 'Description', 'SKU'];
    const rows = bom.map(item => [
      item.component,
      item.quantity,
      item.unit,
      item.description,
      item.sku || '',
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    return csv;
  },
}));
